import React from "react";
import { useEffect, useState } from "react";
import { Button, Col, Form, Pagination, Row, Skeleton, Space, Tag } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import employeeStyles from "./Employee.module.css";
import { useNavigate, useParams } from "react-router";
import { useUserStore } from "../../../../context/UserStore";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";

export default function Employee() {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = useUserStore((state) => state.user);
  const [isLoading, setIsLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [units, setUnits] = useState(null);
  const [managerHierarchy, setManagerHierarchy] = useState([]);
  const [subordinateUnits, setSubordinateUnits] = useState([]);
  const [subordinateUnitPages, setSubordinateUnitPages] = useState({});
  const [isHierarchyLoading, setIsHierarchyLoading] = useState(true);

  const loadEmployee = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/admin/employees/${id}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        type: "json",
      },
    )
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error(response.statusText);
        }
      })
      .then((response) => {
        setEmployee(response.employee.data);
        setHospital(
          response.employee.included.find((item) => item.type === "hospital"),
        );
        setIsLoading(false);

        const newUnitData =
          response.employee.data.attributes.employee_unit_options.map(
            (item) => {
              return {
                label: item.label,
                value: item.value,
                key: item.key,
              };
            },
          );
        setUnits(newUnitData);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const loadManagerHierarchy = async () => {
    await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/admin/employees/${id}/manager_hierarchy`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        type: "json",
      },
    )
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error(response.statusText);
        }
      })
      .then((response) => {
        setManagerHierarchy(response.manager_hierarchy || []);
        setSubordinateUnits(response.subordinate_units || []);
        setSubordinateUnitPages({});
        setIsHierarchyLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setManagerHierarchy([]);
        setSubordinateUnits([]);
        setIsHierarchyLoading(false);
      });
  };

  useEffect(() => {
    setIsLoading(true);
    setIsHierarchyLoading(true);
    loadEmployee();
    loadManagerHierarchy();
  }, [id]);

  const employeesPerPage = 5;

  const getSubordinateUnitPage = (unitId) => subordinateUnitPages[unitId] || 1;

  const onSubordinateUnitPageChange = (unitId, page) => {
    setSubordinateUnitPages((previous) => ({
      ...previous,
      [unitId]: page,
    }));
  };

  return (
    <>
      <div className={`py-5 grid grid-cols-1 mb-5 ${employeeStyles.headerBar}`}>
        <h1 className="text-4xl text-center text-white">View Employee</h1>
      </div>
      <Skeleton loading={isLoading} active>
        {employee && (
          <>
            <PageHeader
              ghost={false}
              onBack={() => navigate(-1)}
              title={employee.attributes.full_name}
              extra={[
                <Space size="small">
                  <Button
                    key="back"
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold px-4 rounded"
                    onClick={() => navigate("/admin/employees")}
                  >
                    Back
                  </Button>
                  <Button
                    key="edit"
                    lassName="bg-orange-500 hover:bg-orange-700 font-bold rounded"
                    onClick={() => navigate(`/admin/employees/${id}/edit`)}
                  >
                    <EditOutlined />
                  </Button>
                  <Button
                    key="delete"
                    type="danger"
                    className="bg-red-500 hover:bg-red-700 font-bold rounded"
                    onClick={() => disableUser(id)}
                  >
                    <DeleteOutlined />
                  </Button>
                </Space>,
              ]}
            ></PageHeader>
            <Row>
              <Col span={20}>
                <Form layout="vertical">
                  <Row>
                    <Col md={10} xs={20}>
                      <Form.Item label="Employee ID">
                        <Form.Item
                          rules={[
                            { required: true, message: "Please choose a Role" },
                          ]}
                        >
                          {employee.attributes.employee_id}
                        </Form.Item>
                      </Form.Item>
                    </Col>
                    <Col md={10} xs={20}>
                      <Form.Item label="Job Title">
                        <Form.Item
                          rules={[
                            { required: true, message: "Please choose a Role" },
                          ]}
                        >
                          {employee.attributes.job_title}
                        </Form.Item>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={10} xs={20}>
                      <Form.Item label="Email">
                        <Form.Item
                          rules={[
                            { required: true, message: "Please choose a Role" },
                          ]}
                        >
                          {employee.attributes.email}
                        </Form.Item>
                      </Form.Item>
                    </Col>
                    <Col md={10} xs={20}>
                      <Form.Item label="Conversation status">
                        <Form.Item
                          rules={[
                            { required: false, message: "Conversation status" },
                          ]}
                        >
                          {employee.attributes.conversation_status_label}
                        </Form.Item>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={10} xs={20}>
                      <Form.Item label="Conversation cadence">
                        <Form.Item
                          rules={[
                            { required: false, message: "Conversation cadence" },
                          ]}
                        >
                          {employee.attributes.conversation_cadence_days} days
                        </Form.Item>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={10} xs={20}>
                      <Form.Item label="Notes">
                        <Form.Item
                          rules={[
                            {
                              required: false,
                              message: "Please choose a Role",
                            },
                          ]}
                        >
                          {employee.attributes.notes}
                        </Form.Item>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={10} xs={20}>
                      <Form.Item label="Hospital">
                        <Form.Item
                          rules={[
                            { required: true, message: "Please choose a Role" },
                          ]}
                        >
                          {hospital && (
                            <Button
                              type="link"
                              className="bg-blue-500 hover:bg-blue-700 text-white font-bold px-4 rounded"
                              onClick={() =>
                                navigate(
                                  `/admin/hospitals/${hospital.attributes.id}`,
                                )
                              }
                            >
                              {hospital.attributes.name}
                            </Button>
                          )}
                        </Form.Item>
                      </Form.Item>
                    </Col>
                    <Col md={10} xs={20}>
                      <Form.Item label="Units">
                        <Form.Item
                          rules={[
                            { required: true, message: "Please choose a Role" },
                          ]}
                        >
                          {units &&
                            units.map((item) => (
                              <Tag color="blue" key={item.key}>
                                {item.label}
                              </Tag>
                            ))}
                        </Form.Item>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
                <div className={employeeStyles.hierarchySection}>
                  <h2 className={employeeStyles.hierarchyTitle}>
                    Manager Hierarchy
                  </h2>
                  <Skeleton loading={isHierarchyLoading} active>
                    {managerHierarchy.length === 0 &&
                    subordinateUnits.length === 0 ? (
                      <p className={employeeStyles.hierarchyEmpty}>
                        No manager hierarchy found for this employee.
                      </p>
                    ) : (
                      <div className={employeeStyles.hierarchyGraph}>
                        {[...managerHierarchy].reverse().map((level, index) => (
                          <div key={`hierarchy-level-${index}`}>
                            <div className={employeeStyles.hierarchyLevel}>
                              {level.map((manager) => (
                                <div
                                  className={employeeStyles.hierarchyNode}
                                  key={`manager-${manager.id}-${index}`}
                                >
                                  {manager.employee_record_id ? (
                                    <Button
                                      type="link"
                                      className={employeeStyles.employeeNameLink}
                                      onClick={() =>
                                        navigate(
                                          `/admin/employees/${manager.employee_record_id}`,
                                        )
                                      }
                                    >
                                      {manager.full_name}
                                    </Button>
                                  ) : (
                                    <div className={employeeStyles.nodeName}>
                                      {manager.full_name}
                                    </div>
                                  )}
                                  <div className={employeeStyles.nodeRole}>
                                    {manager.job_title || "Manager"}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className={employeeStyles.hierarchyConnector}>
                              |
                            </div>
                          </div>
                        ))}
                        <div
                          className={`${employeeStyles.hierarchyNode} ${employeeStyles.employeeNode}`}
                        >
                          <div className={employeeStyles.nodeName}>
                            {employee.attributes.full_name}
                          </div>
                          <div className={employeeStyles.nodeRole}>
                            {employee.attributes.job_title || "Employee"}
                          </div>
                        </div>
                        {subordinateUnits.length > 0 && (
                          <>
                            <div className={employeeStyles.hierarchyConnector}>
                              |
                            </div>
                            <div className={employeeStyles.hierarchyLevel}>
                              {subordinateUnits.map((unit) => (
                                (() => {
                                  const page = getSubordinateUnitPage(unit.unit_id);
                                  const employees = unit.employees || [];
                                  const start = (page - 1) * employeesPerPage;
                                  const pagedEmployees = employees.slice(
                                    start,
                                    start + employeesPerPage,
                                  );

                                  return (
                                    <div
                                      className={`${employeeStyles.hierarchyNode} ${employeeStyles.reportNode}`}
                                      key={`subordinate-unit-${unit.unit_id}`}
                                    >
                                      <div className={employeeStyles.nodeName}>
                                        {unit.unit_name}
                                      </div>
                                      <ul className={employeeStyles.employeeNameList}>
                                        {pagedEmployees.map((employeePreview) => (
                                          <li
                                            key={`employee-name-${unit.unit_id}-${employeePreview.id}`}
                                            className={employeeStyles.employeeNameListItem}
                                          >
                                            <Button
                                              type="link"
                                              className={employeeStyles.employeeNameLink}
                                              onClick={() =>
                                                navigate(
                                                  `/admin/employees/${employeePreview.id}`,
                                                )
                                              }
                                            >
                                              {employeePreview.full_name}
                                            </Button>
                                          </li>
                                        ))}
                                      </ul>
                                      {employees.length > employeesPerPage && (
                                        <Pagination
                                          className={employeeStyles.unitPagination}
                                          current={page}
                                          pageSize={employeesPerPage}
                                          total={employees.length}
                                          size="small"
                                          onChange={(newPage) =>
                                            onSubordinateUnitPageChange(
                                              unit.unit_id,
                                              newPage,
                                            )
                                          }
                                          showSizeChanger={false}
                                        />
                                      )}
                                    </div>
                                  );
                                })()
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </Skeleton>
                </div>
              </Col>
            </Row>
          </>
        )}
      </Skeleton>
    </>
  );
}
